import axios, {AxiosInstance, AxiosRequestConfig, Method} from "axios";
import {ErrorLocationType, HttpType} from "@/utilities/enums/Enums";
import {baseURL} from "@/AppConfig";
import {PathHelper} from "@/utilities/helpers/PathHelper";
import AuthService from "@/utilities/services/AuthService";
import {GeneralOldResponse} from "@/models/api/old/responses";
import {useGuestStore} from "@/stores/GuestStore";
import {Block} from "@/models/helpers/Block";
import {currentRouteIsExternal} from "@/utilities/helpers/RoutingHelper";
import GeneralResponse from "@/models/api/GeneralResponse";


export type HttpClientResponse<T> = [null, T] | [Error, any]

export class WebCallService {
    private axiosInstance : AxiosInstance;
    private url: string;
    private readonly baseUrl: string;
    private authService:AuthService|null;
    private pathHelper:PathHelper;

    constructor(needsAuthService:boolean = true){
        this.url = '';
        this.baseUrl = baseURL+'/api';
        this.axiosInstance = axios.create({
            withCredentials: true,
            baseURL: this.baseUrl
        });
        this.authService = needsAuthService ? AuthService.getDefaultInstance() : null;
        this.pathHelper = new PathHelper();
    }

    public async httpSend<T = unknown>(type:HttpType, uri:string, body:any = null):Promise<T>{
        let response;

        switch(type){
            case HttpType.post: {
                response = await this.sendPostRequest<T>(uri, body);
                break;
            }
            case HttpType.get: {
                response = await this.sendGetRequest<T>(uri);
                break;
            }
            case HttpType.put: {
                response = await this.sendPutRequest<T>(uri, body);
                break;
            }
            case HttpType.delete: {
                response = await this.sendDeleteRequest<T>(uri, body);
                break;
            }
        }

        return this.convertErrorIfNeeded(response);
    }

    public async sendGetRequest<T = unknown>(path: string): Promise<HttpClientResponse<T>>
    {
        return await this.axiosCall(await this.getRequestConfig("get", path, null));
    }

    public async sendPostRequest<T = unknown>(path: string, bodyContent: any): Promise<HttpClientResponse<T>>
    {
        return await this.axiosCall<T>(await this.getRequestConfig("post", path, bodyContent));
    }

    public async sendPutRequest<T = unknown>(path: string, bodyContent: any): Promise<HttpClientResponse<T>>
    {
        return await this.axiosCall<T>(await this.getRequestConfig("put", path, bodyContent));
    }

    public async sendDeleteRequest<T = unknown>(path: string, bodyContent: any): Promise<HttpClientResponse<T>>
    {
        return await this.axiosCall<T>(await this.getRequestConfig("delete", path, bodyContent));
    }

    /*
    *   creates new axios instance and cleanly gets from an external API without response conversion or token workflow
    */
    public async getFromExternalAPI(uri:string, withCredentials:boolean = false):Promise<any>{
        return withCredentials
            ? await axios.create().get(uri, {withCredentials:true})
            : await axios.create().get(uri);
    }

    public async uploadBlock(uploadGuid:string, block:Block, url:string, errorLocationType:ErrorLocationType, callback:Function|null = null, abortSignal:AbortSignal):Promise<string|GeneralOldResponse>{
        try {
            const contentType = "multipart/form-data";
            const token = currentRouteIsExternal()
                ? (useGuestStore()).token
                : await this.authService!.getAccessToken();
            const authorizationHeader = `Bearer ${token}`;

            const body = new FormData();
            body.append("block", block.partialFile);

            const resp = await this.axiosInstance.request({
                method: "post",
                url: url,
                data: body,
                headers: {
                    'Content-Type': contentType,
                    'Authorization': authorizationHeader,
                },
                timeout: 0, //disable timeout
                signal: abortSignal, //wire for aborting the request
                onUploadProgress: (p:ProgressEvent) => {
                    if(callback === null) return
                    callback(uploadGuid,block.index, errorLocationType,`${~~((p.loaded / p.total)*100)}%`)
                },
            })

            const success = resp.status >= 200 && resp.status <= 299
            if(!success) {
                return new GeneralOldResponse(false, "client.InitFile", resp.status)
            }

            return resp.data as string;
        }
        catch (e:any) {
            if (e.message === "canceled") throw e;
            return new GeneralOldResponse(false, `Caught: ${e as string}`, 555);
        }
    }

    public async downloadFile(url:string, callback:Function|null = null):Promise<GeneralOldResponse>{
        try {
            const token = currentRouteIsExternal()
                ? (useGuestStore()).token
                : await this.authService!.getAccessToken();
            const authorizationHeader = `Bearer ${token}`;

            const resp = await this.axiosInstance.request({
                method: "get",
                responseType: "blob",
                url: url,
                headers: {
                    'Authorization': authorizationHeader,
                },
                timeout: 0, //disable timeout
                onDownloadProgress: (p:ProgressEvent) => {
                    if(callback === null) return
                    callback(`${~~((p.loaded / p.total)*100)}%`)
                }
            })

            const success = resp.status >= 200 && resp.status <= 299
            if (!success) {
                return new GeneralOldResponse(false, "client.downloadFile", resp.status)
            }

            // extract fileName from responseHeader
            const fileName = resp.headers["content-disposition"]
                .split(";")
                .filter(s => s.includes("filename="))[0]
                .split("=")[1]
                .replace(/"/g, '');

            // create blob from resp and then automate a download click via DOM
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(resp.data as Blob);
            link.download = fileName;
            link.click();
            link.remove();

            return new GeneralOldResponse(true, "", resp.status);
        }
        catch (e) {
            return new GeneralOldResponse(false, `Caught: ${e as string}`, -1)
        }
    }

    public async downloadFileWithBody(url:string, bodyContent:any, callback:Function|null = null):Promise<GeneralOldResponse>{
        try {
            const token = currentRouteIsExternal()
                ? (useGuestStore()).token
                : await this.authService!.getAccessToken();
            const authorizationHeader = `Bearer ${token}`;

            const resp = await this.axiosInstance.request({
                method: "post",
                responseType: "blob",
                url: url,
                data: bodyContent,
                headers: {
                    'Authorization': authorizationHeader,
                },
                timeout: 0, //disable timeout
                onDownloadProgress: (p:ProgressEvent) => {
                    if(callback === null) return
                    callback(`${~~((p.loaded / p.total)*100)}%`)
                }
            })

            const success = resp.status >= 200 && resp.status <= 299
            if (!success) {
                return new GeneralOldResponse(false, "client.downloadFile", resp.status)
            }

            // extract fileName from responseHeader
            const fileName = resp.headers["content-disposition"]
                .split(";")
                .filter(s => s.includes("filename="))[0]
                .split("=")[1]
                .replace(/"/g, '');

            // create blob from resp and then automate a download click via DOM
            const link = document.createElement("a");
            link.href = window.URL.createObjectURL(resp.data as Blob);
            link.download = fileName;
            link.click();
            link.remove();

            return new GeneralOldResponse(true, "", resp.status);
        }
        catch (e) {
            return new GeneralOldResponse(false, `Caught: ${e as string}`, -1)
        }
    }

    /* --- PRIVATE --- */

    private async axiosCall<T = unknown>(config: AxiosRequestConfig): Promise<HttpClientResponse<T>>
    {

        try {
            const req = await this.axiosInstance.request<T>(config);
            return [null, req.data];
        }
        catch (error:any) {
            return [new Error(error as string), error.response]; //return response as data to read HttpCode
        }
    }

    private async getRequestConfig(requestMethod: Method, path: string, bodyContent: any) : Promise<AxiosRequestConfig>
    {
        this.url = `${this.baseUrl}/${path}`;

        const token = currentRouteIsExternal()
            ? (useGuestStore()).token
            : await this.authService!.getAccessToken();
        const authorizationHeader: string = 'Bearer ' + token;

        if(requestMethod === "get")
        {
            // clean up auth header if startup config gets requested
            if(path ===  this.pathHelper.misc.getStartupConfigPath()){
                return {
                    method: requestMethod,
                    url: this.url,
                };
            }

            return {
                method: requestMethod,
                url: this.url,
                headers: {
                    'Authorization': authorizationHeader,
                }
            }
        }

        const contentType = "application/json";

        return {
            method: requestMethod,
            url: this.url,
            data: bodyContent,
            headers: {
                'Content-Type': contentType,
                'Authorization': authorizationHeader,
            }
        }
    }

    // convert error to generalResponse if needed
    // needs to be any since wrapping usage is type-dynamic
    private convertErrorIfNeeded(resp:HttpClientResponse<any>):any {
        const error = resp[0];
        const data = resp[1];

        return error ? new GeneralResponse(data.data.requestId, data.data.success, data.data.context, data) : data;
    }
}
